<template>
  <div class="container py-5">

    <div class="buttons">
      <button
        @click="adding = true"
        class="button is-primary">
        Add a Sample
      </button>
      <a class="button"
        v-if="selected.length > 0"
        @click="launchConfirmDelete()">
        <span class="icon is-medium">
          <i class="fas fa-trash has-text-danger"></i>
        </span>
        <span>Delete {{ selected.length }} Selected Samples</span>
      </a>
    </div>

    <div class="tabs mt-0">
      <ul>
        <li 
          v-for="reason in requestReasons"
          :key="reason"
          @click="setFilter({
            field: 'sample_type', 
            value: reason
          })"
          :class="{ 'is-active': filters.sample_type == reason }">
          <a>{{ reason }} Samples</a>
        </li>
      </ul>
    </div>

    <article 
      v-if="!loading && samples.length == 0" 
      class="message is-info is-fullwidth">
      <div class="message-body">
        There are no samples.
      </div>
    </article>

    <v-fade-transition v-else mode="out-in">
      <table 
        v-if="samples.length > 0"
        :key="filters.sample_type"
        class="table is-fullwidth is-hoverable has-small-font">

        <thead>
          <tr>
            <th>
              <v-checkbox 
                :value="allSelected" 
                @input="toggleAllSelected($event)"/>
            </th>
            <th 
              v-for="(header, headerIndex) in headers"
              :key="headerIndex">
              {{ header }}
            </th>
            <th></th>
          </tr>
        </thead>

        <tbody>
          <tr
            class="v-centered"
            v-for="sample in samples"
            :key="sample.id">

            <td>
              <v-checkbox 
                @click.native.stop=""
                :value="selected.indexOf(sample.id) != -1"
                @input="toggleSelected(sample.id)"/>
            </td>
            <td v-if="filters.sample_type == 'Introduction'">
              <v-text-field 
                v-if="editingId == sample.id"
                required
                v-model="sample.name"
                placeholder="Sample Name"/>
              <span v-else>
                {{ sample.name }}
              </span>
            </td>
            <td>
              <div 
                v-if="sample.product_master"
                class="flex flex--row flex--align-center">
                <a 
                  class="mr-2 flex flex--row flex--align-center"
                  @click.stop=""
                  :href="sample.product_master.image"
                  target="_blank"
                  v-if="sample.product_master.image">
                  <img 
                    width="30px"
                    height="auto"
                    :alt="sample.product_master.name"
                    :src="sample.product_master.image"/>
                </a>
                <div>
                  {{ sample.product_master ? sample.product_master.name : null }}
                </div>
              </div>
            </td>
            <td>
              <v-text-field 
                v-if="editingId == sample.id"
                required
                v-model="sample.pattern"
                placeholder="Sample Pattern"/>
              <span v-else>{{ sample.pattern }}</span>
            </td>
            <td>
              <v-text-field 
                v-if="editingId == sample.id"
                required
                v-model="sample.color"
                placeholder="Sample Color"/>
              <span v-else>{{ sample.color }}</span>
            </td>
            <td>
              {{ sample.sample_type }}
            </td>
            <td v-if="filters.sample_type == 'Project'">
              <template v-if="sample.project">
                <div class="text--nowrap">
                  {{ sample.project.project_name }}
                </div>
                <div class="help has-text-grey">
                  {{ sample.project.user.display_name }}
                </div>
              </template>
            </td>
            <td>
              <v-text-field
                required
                v-if="editingId == sample.id"
                type="number"
                placeholder="Quantity available"
                v-model="sample.qty_available"/>
              <span v-else>
                {{ sample.qty_available }}
              </span>
            </td>
            <td>
              {{ sample.qty_with_clients }}
            </td>
            <td>
              {{ sample.total_qty }}
            </td>
            <td>
              <v-text-field
                required
                v-if="editingId == sample.id"
                placeholder="Shelf Number"
                v-model="sample.shelf_number"/>
              <span v-else>
                {{ sample.shelf_number }}
              </span>
            </td>

            <td>
              <div key="not-editing" class="buttons buttons-right" v-if="editingId != sample.id">
                <button 
                  @click="editingId = sample.id"
                  class="button is-small is-primary">
                  <span class="icon">
                    <i class="fas fa-edit"></i>
                  </span>
                </button>
                <button 
                  @click.stop="launchConfirmDelete(sample.id)"
                  class="button is-small has-text-grey">
                  <span class="icon">
                    <i class="fas fa-trash"></i>
                  </span>
                </button>
              </div>

              <div key="editing" class="buttons buttons-right" v-else> 
                <button 
                  @click.stop="update(sample)"
                  class="button is-small is-primary">
                  <span class="icon">
                    <i class="fas fa-save"></i>
                  </span>
                </button>
                <button 
                  @click.stop="editingId = null"
                  class="button is-small">
                  <span class="icon">
                    <i class="fas fa-times has-text-grey"></i>
                  </span>
                </button>
              </div>
            </td>

          </tr>
        </tbody>

      </table>
    </v-fade-transition>

    <add-sample-modal v-model="adding"/>

    <v-pagination 
      v-if="pagination.pages > 1"
      :value="pagination.page"
      @input="changePage"
      :length="pagination.pages"/>

    <v-confirm-modal 
      v-model="confirmDelete.state"
      :title="confirmDelete.title"
      :message="confirmDelete.message"
      :type="confirmDelete.type"
      @confirm="deleteConfirmedCb ? deleteConfirmedCb() : null"
      @cancel="confirmDelete.state = false"/>

  </div>
</template>

<script>
import VCheckbox from '@/components/base/VCheckbox'
import VConfirmModal from '@/components/base/VConfirmModal'
import VPagination from '@/components/base/VPagination'
import VTextField from '@/components/base/VTextField'
import VFadeTransition from '@/components/transitions/VFadeTransition'

import AddSampleModal from '@/components/sampleRequests/AddSampleModal'

import { mapState, mapActions, mapMutations } from 'vuex'
import { parseISO, format } from 'date-fns'

export default {
  components: { 
    VCheckbox,
    VConfirmModal, 
    VPagination,
    VTextField,
    VFadeTransition,
    AddSampleModal
  },
  data() {
    return {
      confirmDelete: {
        state: false,
        title: 'Delete Confirmation',
        message: 'Are you sure you want to delete this sample?',
        type: 'danger'
      },
      deleteCb: null,
      parse: (d) => {
        return d ? parseISO(d) : null
      },
      format: (d) => {
        return d ? format(d, 'yyyy-MM-dd') : null
      },
      editingId: null,
      adding: false
    }
  },
  computed: {
    ...mapState("samples", [
      "samples", "pagination", "selected", "loading", "allSelected", "filters"
    ]),
    ...mapState("sampleRequests", ["requestReasons"]),
    headers() {
      if (this.filters.sample_type == 'Project') {
        return [
          'Material', 'Pattern', 'Color', 'Sample Type', 'Project',
          'Qty Available', 'Qty with Clients', 'Total Qty', 'Shelf Number'
        ]
      } else {
        return [
          'Name', 'Material', 'Pattern', 'Color', 'Sample Type',
          'Qty Available', 'Qty with Clients', 'Total Qty', 'Shelf Number'
        ]
      }
    }
  },
  watch: {
    'pagination.page'() {
      let params = {...this.pagination, ...this.filters}
      this.getSamples(params);
    },
    filters: {
      handler(filters) {
        this.getSamples(filters);
      },
      deep: true
    }
  },
  created() {
    this.initialize();
  },
  methods: {
    ...mapActions("samples", [
      "getSamples", "updateSample", "deleteSample", "toggleAllSelected"
    ]),
    ...mapMutations("samples", ["changePage", "toggleSelected", "setFilter"]),
    initialize() {
      this.getSamples(this.filters);
    },
    update(sample) {
      this.updateSample({ 
        sampleId: sample.id, 
        data: {
          name: sample.name,
          pattern: sample.pattern,
          color: sample.color,
          total_qty: sample.total_qty,
          qty_with_clients: sample.qty_with_clients,
          qty_available: sample.qty_available,
          shelf_number: sample.shelf_number
        }
      });
    },
    viewSample(sample) {
      this.$router.push({
        name: 'SampleViewer',
        params: {
          sampleId: sample.id,
          sample: sample
        }
      })
    },
    launchConfirmDelete(id) {
      if (!id) {
        this.deleteConfirmedCb = () => {
          this.selected.forEach(id => {
            this.deleteSample(id);
            this.confirmDelete.state = false;
          })
        }
        this.confirmDelete.message = `Are you sure you want to delete the ${this.selected.length} samples?`;
        this.confirmDelete.state = true;
      } else {
        this.deleteConfirmedCb = () => {
          this.deleteSample(id);
          this.confirmDelete.state = false;
        }
        this.confirmDelete.message = 'Are you sure you want to delete this sample?';
        this.confirmDelete.state = true;
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/table.scss';
@import '@/styles/flexbox.scss';
</style>
